import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { ApiError } from 'src/error/ApiError';
import { useGenerateMeetingNote } from '../hooks/useGenerateMeetingNote';
import { resolveAdditionalInstructions } from '../utils/additionalInstructions';
import { MeetingNoteStepProps } from '../types';

type Props = MeetingNoteStepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function NoteProcessingStep({ data, setData, onSubmittingChange }: Props) {
  const { generateMeetingNote } = useGenerateMeetingNote();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didSubmitRef = useRef(false);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submit = async () => {
    if (!data.selectedRecordingId) {
      notifyAndLogError(
        'No source selected',
        'Please select a source and try again.',
        new Error('Missing recording ID'),
      );
      return;
    }
    if (!data.template && !data.selectedAgendaFileId) {
      notifyAndLogError(
        'No template or agenda selected',
        'Please select a template or agenda and try again.',
        new Error('Missing template/agenda ID'),
      );
      return;
    }

    const notePayload = {
      dataUploadFileId: data.selectedRecordingId,
      templateId: data.template?.id,
      agendaFileId: data.selectedAgendaFileId,
      agendaInstruction: data.agendaInstruction?.trim() || undefined,
      additionalInstructions: resolveAdditionalInstructions(
        data.template?.eligibleForSummarisation,
        data.additionalInstructions,
      ),
      summarise: data.summarise,
    };

    setIsSubmitting(true);
    try {
      let response;
      if (data.isFamily) {
        if (!data.family) {
          notifyAndLogError(
            'No family selected',
            'Please select a family and try again.',
            new Error('Missing family ID'),
          );
          return;
        }
        response = await generateMeetingNote(data.family.id, 'family', notePayload);
      } else {
        if (!data.client?.id) {
          notifyAndLogError(
            'No client selected',
            'Please select a client and try again.',
            new Error('Missing client ID'),
          );
          return;
        }
        response = await generateMeetingNote(data.client.id, 'client', notePayload);
      }
      setData(prev => ({ ...prev, fileOwnerClientId: response.data.fileOwnerClientId }));
      dispatchAppEvent('MEETING_NOTE_GENERATED');
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        notifyAndLogError(
          'File still processing',
          error.message || 'This file is still being processed. Please try again once it is ready.',
          error,
        );
      } else {
        notifyAndLogError('Failed to generate meeting note', 'Please try again.', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasId = () => {
    if (data.isFamily) {
      return !!data.family?.id;
    } else {
      return !!data.client?.id;
    }
  };

  useEffect(() => {
    if (!hasId() || data.fileOwnerClientId || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submit();
  }, [data]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
